/**
 * Audit Service — Expanded audit trail for CVM Instrução 21 Art. 16 compliance.
 *
 * Categories: auth, navigation, data_access, data_mutation, admin,
 *             compliance, financial, system, general
 * Severity:   info, warning, critical
 *
 * All inserts go through the `log_audit_event` RPC (SECURITY DEFINER)
 * which automatically captures the authenticated user context.
 */

import { supabase } from "./core.js";
import { logError } from "../shared/logger.js";

// ── Types ──────────────────────────────────────────────────────────

export type AuditCategory =
  | "auth"
  | "navigation"
  | "data_access"
  | "data_mutation"
  | "admin"
  | "compliance"
  | "financial"
  | "system"
  | "general";

export type AuditSeverity = "info" | "warning" | "critical";

export interface AuditLogEntry {
  id: string;
  user_id: string | null;
  client_id: string | null;
  action: string;
  category: AuditCategory;
  severity: AuditSeverity;
  details: Record<string, unknown>;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
}

export interface LogAuditOptions {
  category?: AuditCategory;
  severity?: AuditSeverity;
  details?: Record<string, unknown>;
  clientId?: string;
}

// ── Core Logger ────────────────────────────────────────────────────

/**
 * Log an audit event via server-side RPC.
 * Non-blocking — errors are captured but never thrown.
 */
export async function logAudit(action: string, opts: LogAuditOptions = {}): Promise<void> {
  try {
    const { category = "general", severity = "info", details = {}, clientId } = opts;
    const { error } = await supabase.rpc("log_audit_event", {
      p_action: action,
      p_category: category,
      p_severity: severity,
      p_details: details,
      p_client_id: clientId ?? null,
    });
    if (error) logError("audit.logAudit", error);
  } catch (e) {
    logError("audit.logAudit", e as Error);
  }
}

// ── Convenience Wrappers ───────────────────────────────────────────

export const auditAuth = (action: string, details?: Record<string, unknown>) =>
  logAudit(action, { category: "auth", details });

export const auditNav = (action: string, details?: Record<string, unknown>) =>
  logAudit(action, { category: "navigation", details });

export const auditDataAccess = (action: string, details?: Record<string, unknown>) =>
  logAudit(action, { category: "data_access", details });

export const auditDataMutation = (
  action: string,
  details?: Record<string, unknown>,
  severity: AuditSeverity = "info"
) => logAudit(action, { category: "data_mutation", severity, details });

export const auditAdmin = (
  action: string,
  details?: Record<string, unknown>,
  severity: AuditSeverity = "info"
) => logAudit(action, { category: "admin", severity, details });

export const auditCompliance = (
  action: string,
  details?: Record<string, unknown>,
  severity: AuditSeverity = "warning"
) => logAudit(action, { category: "compliance", severity, details });

export const auditFinancial = (
  action: string,
  details?: Record<string, unknown>,
  severity: AuditSeverity = "info"
) => logAudit(action, { category: "financial", severity, details });

// ── Query Functions ────────────────────────────────────────────────

export interface FetchAuditOptions {
  limit?: number;
  offset?: number;
  category?: AuditCategory;
  severity?: AuditSeverity;
  clientId?: string;
  from?: string; // ISO date
  to?: string; // ISO date
}

export async function fetchAuditLogs(opts: FetchAuditOptions = {}): Promise<AuditLogEntry[]> {
  const { limit = 50, offset = 0, category, severity, clientId, from, to } = opts;

  let query = supabase
    .from("audit_logs")
    .select("id, user_id, client_id, action, category, severity, details, created_at")
    .order("created_at", { ascending: false })
    .range(offset, offset + limit - 1);

  if (category) query = query.eq("category", category);
  if (severity) query = query.eq("severity", severity);
  if (clientId) query = query.eq("client_id", clientId);
  if (from) query = query.gte("created_at", from);
  if (to) query = query.lte("created_at", to);

  const { data, error } = await query;
  if (error) throw error;
  return (data || []) as AuditLogEntry[];
}

/**
 * Count audit events by category (for admin dashboard).
 */
export async function fetchAuditStats(): Promise<Record<AuditCategory, number>> {
  const { data: counts, error: countErr } = await supabase
    .from("audit_logs")
    .select("category")
    .gte("created_at", new Date(Date.now() - 30 * 86400000).toISOString());

  if (countErr) throw countErr;

  const stats: Record<string, number> = {};
  for (const row of counts || []) {
    const cat = (row as Record<string, unknown>).category as string;
    stats[cat] = (stats[cat] || 0) + 1;
  }
  return stats as Record<AuditCategory, number>;
}
